import { CATALOG_SEARCH_BOUNDS, SEARCH_DOCUMENT_VERSION } from "./catalog-search.js";
import { FOLD_VERSION, foldSearchText } from "./catalog-search-text.js";
import { sha256HexOfText } from "./sha256.js";
const { document: LIMITS } = CATALOG_SEARCH_BOUNDS;
/** Cuts at a number of code points (Postgres `char_length` counts code points, not UTF-16 units). */
function truncate(text, max) {
    const points = Array.from(text);
    if (points.length <= max)
        return text;
    return points.slice(0, max).join("").trim();
}
let regionNames;
/** The English name of an ISO 3166 region code, or `null` when the runtime has none. */
function countryName(code) {
    if (regionNames === undefined) {
        try {
            regionNames = new Intl.DisplayNames(["en"], { type: "region" });
        }
        catch {
            regionNames = null;
        }
    }
    try {
        const name = regionNames?.of(code);
        return name === undefined || name === code ? null : name;
    }
    catch {
        return null;
    }
}
const words = (label) => label.replaceAll("_", " ").replaceAll("-", " ");
/** Distinct, non-empty terms in first-seen order (a repeated word adds nothing to a weight). */
function distinct(values) {
    const seen = new Set();
    const out = [];
    for (const value of values) {
        const folded = foldSearchText(value ?? "");
        if (folded === "" || seen.has(folded))
            continue;
        seen.add(folded);
        out.push(folded);
    }
    return out;
}
function subtypeTerms(subtype) {
    switch (subtype?.type) {
        case "DINING":
            return {
                terms: [subtype.dressCode === null ? null : words(subtype.dressCode)],
                notes: [subtype.reservationPolicy === null ? null : words(subtype.reservationPolicy)],
            };
        case "EVENT":
            return { terms: [subtype.venueName], notes: [] };
        case "EXPERIENCE":
            return { terms: [subtype.privacy === null ? null : words(subtype.privacy)], notes: [] };
        case "SERVICE":
            return { terms: [subtype.serviceAreaText], notes: [subtype.availabilityNotes] };
        default:
            return { terms: [], notes: [] };
    }
}
/** Builds the document of an item. Pure and deterministic: the same input is the same hash. */
export function buildCatalogSearchDocument(input) {
    const nameKey = truncate(foldSearchText(input.name), LIMITS.name);
    const nameTokens = new Set(nameKey.split(" "));
    const slugWords = foldSearchText(words(input.slug))
        .split(" ")
        .filter((word) => word !== "" && !nameTokens.has(word));
    const nameText = truncate([nameKey, ...slugWords].join(" "), LIMITS.name);
    const country = input.countryCode === null ? null : countryName(input.countryCode);
    const { terms: subtypeTermList, notes: subtypeNotes } = subtypeTerms(input.subtype);
    const termsText = truncate(distinct([
        input.itemType,
        ...input.categories.map((category) => category.name),
        ...input.tags.map((tag) => tag.label),
        input.locality,
        input.region,
        input.countryCode,
        country,
        ...input.people.map((person) => person.displayName),
        ...subtypeTermList,
    ]).join(" "), LIMITS.terms);
    const summaryText = truncate(foldSearchText(input.shortDescription ?? ""), LIMITS.summary);
    const bodyText = truncate(distinct([input.description, ...subtypeNotes]).join(" "), LIMITS.body);
    const localityFolded = foldSearchText(input.locality ?? "");
    const localityKey = localityFolded === "" ? null : truncate(localityFolded, 100);
    const contentSha256 = sha256HexOfText(JSON.stringify([
        SEARCH_DOCUMENT_VERSION,
        FOLD_VERSION,
        nameKey,
        localityKey,
        nameText,
        termsText,
        summaryText,
        bodyText,
    ]));
    return {
        documentVersion: SEARCH_DOCUMENT_VERSION,
        contentSha256,
        nameKey,
        localityKey,
        nameText,
        termsText,
        summaryText,
        bodyText,
    };
}
/**
 * The text an embedder reads for a document: its parts in weight order, one per line, cut to the
 * embedder's limit. The same document always gives the same text.
 */
export function catalogSearchEmbeddingText(document) {
    const text = [document.nameText, document.termsText, document.summaryText, document.bodyText]
        .filter((part) => part !== "")
        .join("\n");
    return truncate(text, CATALOG_SEARCH_BOUNDS.embedderTextChars);
}
